import {
  faChevronDown,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { Button, ButtonActionProps } from '../Button'
import { Menu } from './Menu'
import { useIsMobile } from '../../hooks'
import { Modal } from '../Modal'
import { createContext, ReactNode, useContext, useRef, useState } from 'react'

type ButtonMenuContextValue = {
  close: () => void
  /** True when a `useButtonMenu()` consumer is rendered inside a `ButtonMenu`. */
  insideMenu: boolean
  /** Render a node OUTSIDE the menu's (unmounting) popup so it survives the menu
   *  closing — e.g. a confirm modal opened from a menu item. Pass null to clear. */
  setOverlay: (node: ReactNode) => void
}
const ButtonMenuContext = createContext<ButtonMenuContextValue>({
  close: () => {},
  insideMenu: false,
  setOverlay: () => {},
})
export const useButtonMenu = () => useContext(ButtonMenuContext)

/**
 *  Really simple component for a button with a popup menu
 */
export const ButtonMenu = ({
  children,
  buttonText,
  closeOnMenuClick,
  label,
  icon,
  ...props
}: {
  buttonText?: React.ReactNode
  closeOnMenuClick?: boolean
  label?: string
  icon?: IconDefinition
} & Omit<ButtonActionProps, 'onClick' | 'ref'>) => {
  const ref = useRef<HTMLButtonElement>(null)
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [overlay, setOverlay] = useState<ReactNode>(null)
  const close = () => setOpen(false)
  return (
    <>
      <Button
        icon={icon || faChevronDown}
        iconPosition='right'
        onClick={() => setOpen(true)}
        ref={ref}
        {...props}>
        {buttonText}
      </Button>
      <ButtonMenuContext.Provider value={{ close, insideMenu: true, setOverlay }}>
        {!isMobile ? (
          <Menu isOpen={open} onClose={close} triggerRef={ref}>
            <div onClick={() => closeOnMenuClick && close()}>
              {children}
            </div>
          </Menu>
        ) : (
          <Modal
            isOpen={open}
            onClose={close}
            title={label || buttonText}
            type='modal'>
            <div
              className='p-4'
              onClick={() => closeOnMenuClick && close()}>
              {children}
            </div>
          </Modal>
        )}
        {/* Lives outside the Menu/Modal so a confirm dialog opened from an item
            survives the menu closing (the Menu unmounts its children on close). */}
        {overlay}
      </ButtonMenuContext.Provider>
    </>
  )
}
